import * as THREE from 'three';
import { getSharedStandardMaterial } from '../../../../utils/threeResourcePool';
import { FAB_COLORS } from './fabColors';

/** Reference smoked-glass PBR — dark blue-gray, semi-transparent, shows internals. */
export const FAB_SMOKED_GLASS = {
  primary: FAB_COLORS.glass,
  side: FAB_COLORS.glassBlue,
  attenuation: '#0A1E2C',
  metalness: 0.06,
  roughness: 0.12,
  transmission: 0.5,
  thickness: 0.44,
  ior: 1.49,
  opacity: 0.68,
  envMapIntensity: 0.8,
  attenuationDistance: 2.4,
} as const;

export const FAB_SMOKED_GLASS_DISTANT = {
  color: FAB_COLORS.glassBlue,
  metalness: 0.08,
  roughness: 0.18,
  opacity: 0.56,
  envMapIntensity: 0.72,
} as const;

/** Reference cleanroom enclosure — white, highly transparent, shows rack internals. */
export const FAB_CLEAR_WHITE_GLASS = {
  primary: FAB_COLORS.whiteGlass,
  side: FAB_COLORS.whiteGlassTint,
  attenuation: '#E2E8F0',
  metalness: 0.02,
  roughness: 0.1,
  transmission: 0.92,
  thickness: 0.14,
  ior: 1.48,
  opacity: 0.32,
  envMapIntensity: 0.72,
  attenuationDistance: 4.5,
} as const;

export const FAB_CLEAR_WHITE_GLASS_DISTANT = {
  color: FAB_COLORS.clearGlass,
  metalness: 0.04,
  roughness: 0.08,
  opacity: 0.34,
  envMapIntensity: 0.72,
} as const;

/** Reference gray color glass — neutral smoked gray for stocker top view / roof panels. */
export const FAB_GRAY_GLASS = {
  primary: '#5E6C7E',
  side: '#718096',
  attenuation: '#4A5568',
  metalness: 0.04,
  roughness: 0.08,
  transmission: 0.78,
  thickness: 0.18,
  ior: 1.5,
  opacity: 0.6,
  envMapIntensity: 0.92,
  attenuationDistance: 3.2,
} as const;

export const FAB_GRAY_GLASS_DISTANT = {
  color: '#5E6C7E',
  metalness: 0.06,
  roughness: 0.12,
  opacity: 0.55,
  envMapIntensity: 0.85,
} as const;

/** Reference stocker internals — pale cyan-blue shelf glass visible through white shell. */
export const FAB_LIGHT_BLUE_GLASS = {
  primary: FAB_COLORS.lightBlueGlass,
  side: FAB_COLORS.lightBlueGlassBright,
  attenuation: '#5AB0D8',
  metalness: 0.05,
  roughness: 0.06,
  transmission: 0.78,
  thickness: 0.16,
  ior: 1.52,
  opacity: 0.48,
  envMapIntensity: 0.78,
  attenuationDistance: 3.6,
} as const;

export const FAB_LIGHT_BLUE_GLASS_DISTANT = {
  color: FAB_COLORS.lightBlueGlass,
  metalness: 0.06,
  roughness: 0.12,
  opacity: 0.46,
  envMapIntensity: 0.78,
} as const;

/** Stocker rack shelves — pista / pistachio green glass. */
export const FAB_PISTA_GREEN_GLASS = {
  primary: FAB_COLORS.pistaGreenGlass,
  side: FAB_COLORS.pistaGreenGlassBright,
  attenuation: '#7BB868',
  metalness: 0.04,
  roughness: 0.1,
  transmission: 0.72,
  thickness: 0.14,
  ior: 1.51,
  opacity: 0.54,
  envMapIntensity: 0.92,
  attenuationDistance: 3.2,
} as const;

export const FAB_PISTA_GREEN_GLASS_DISTANT = {
  color: FAB_COLORS.pistaGreenGlass,
  metalness: 0.06,
  roughness: 0.14,
  opacity: 0.48,
  envMapIntensity: 0.86,
} as const;

/** Rack shelf read — sky blue glass, legible through the enclosure. */
export const FAB_SHELF_SKY_BLUE_GLASS = {
  primary: FAB_COLORS.lightBlueGlass,
  side: FAB_COLORS.lightBlueGlassBright,
  attenuation: '#5AB0D8',
  metalness: 0.06,
  roughness: 0.1,
  transmission: 0.42,
  thickness: 0.14,
  ior: 1.51,
  opacity: 0.68,
  envMapIntensity: 1.06,
  attenuationDistance: 2.8,
} as const;

/** Stocker enclosure — navy blue glass, internals visible through tint. */
export const FAB_NAVY_GLASS = {
  primary: FAB_COLORS.navyGlass,
  side: FAB_COLORS.navyGlassBright,
  attenuation: '#0E2438',
  metalness: 0.08,
  roughness: 0.1,
  transmission: 0.48,
  thickness: 0.36,
  ior: 1.5,
  opacity: 0.74,
  envMapIntensity: 0.84,
  attenuationDistance: 1.9,
} as const;

export const FAB_NAVY_GLASS_DISTANT = {
  color: FAB_COLORS.navyGlass,
  metalness: 0.1,
  roughness: 0.14,
  opacity: 0.64,
  envMapIntensity: 0.8,
} as const;

const physicalCache = new Map<string, THREE.MeshPhysicalMaterial>();

function applyGlassRenderHints(mat: THREE.Material): void {
  mat.depthWrite = false;
  mat.transparent = true;
  mat.side = THREE.DoubleSide;
}

/** Close-range physical smoked glass — stocker bays and process chambers. */
export function createFabSmokedPhysicalGlass(
  opacity = 1,
  cacheKey: string
): THREE.MeshPhysicalMaterial {
  const key = `${cacheKey}:v2:${opacity.toFixed(2)}`;
  let mat = physicalCache.get(key);
  if (!mat) {
    mat = new THREE.MeshPhysicalMaterial({
      color: FAB_SMOKED_GLASS.primary,
      metalness: FAB_SMOKED_GLASS.metalness,
      roughness: FAB_SMOKED_GLASS.roughness,
      transmission: FAB_SMOKED_GLASS.transmission,
      thickness: FAB_SMOKED_GLASS.thickness,
      ior: FAB_SMOKED_GLASS.ior,
      transparent: true,
      opacity: Math.min(FAB_SMOKED_GLASS.opacity, opacity * 0.72),
      envMapIntensity: FAB_SMOKED_GLASS.envMapIntensity,
      specularIntensity: 0.72,
      attenuationColor: new THREE.Color(FAB_SMOKED_GLASS.attenuation),
      attenuationDistance: FAB_SMOKED_GLASS.attenuationDistance,
    });
    applyGlassRenderHints(mat);
    physicalCache.set(key, mat);
  }
  return mat;
}

/** Side accent panels — same smoked family, slightly lighter blue-gray. */
export function createFabSmokedSidePhysicalGlass(
  opacity = 1,
  cacheKey: string
): THREE.MeshPhysicalMaterial {
  const key = `${cacheKey}-side:v2:${opacity.toFixed(2)}`;
  let mat = physicalCache.get(key);
  if (!mat) {
    mat = new THREE.MeshPhysicalMaterial({
      color: FAB_SMOKED_GLASS.side,
      metalness: 0.1,
      roughness: 0.14,
      transmission: 0.42,
      thickness: 0.32,
      ior: 1.47,
      transparent: true,
      opacity: Math.min(0.62, opacity * 0.66),
      envMapIntensity: 0.76,
      specularIntensity: 0.68,
      attenuationColor: new THREE.Color('#0F2430'),
      attenuationDistance: 2.0,
    });
    applyGlassRenderHints(mat);
    physicalCache.set(key, mat);
  }
  return mat;
}

/** Close-range white transparent enclosure glass. */
export function createFabClearWhitePhysicalGlass(
  opacity = 1,
  cacheKey: string
): THREE.MeshPhysicalMaterial {
  const key = `${cacheKey}-clear-white:v2:${opacity.toFixed(2)}`;
  let mat = physicalCache.get(key);
  if (!mat) {
    mat = new THREE.MeshPhysicalMaterial({
      color: FAB_CLEAR_WHITE_GLASS.primary,
      metalness: FAB_CLEAR_WHITE_GLASS.metalness,
      roughness: FAB_CLEAR_WHITE_GLASS.roughness,
      transmission: FAB_CLEAR_WHITE_GLASS.transmission,
      thickness: FAB_CLEAR_WHITE_GLASS.thickness,
      ior: FAB_CLEAR_WHITE_GLASS.ior,
      transparent: true,
      opacity: Math.min(FAB_CLEAR_WHITE_GLASS.opacity, opacity * 0.32),
      envMapIntensity: FAB_CLEAR_WHITE_GLASS.envMapIntensity,
      specularIntensity: 0.45,
      attenuationColor: new THREE.Color(FAB_CLEAR_WHITE_GLASS.attenuation),
      attenuationDistance: FAB_CLEAR_WHITE_GLASS.attenuationDistance,
    });
    applyGlassRenderHints(mat);
    physicalCache.set(key, mat);
  }
  return mat;
}

/** Side/back enclosure sheets — same white glass family. */
export function createFabClearWhiteSidePhysicalGlass(
  opacity = 1,
  cacheKey: string
): THREE.MeshPhysicalMaterial {
  const key = `${cacheKey}-clear-white-side:v4:${opacity.toFixed(2)}`;
  let mat = physicalCache.get(key);
  if (!mat) {
    mat = new THREE.MeshPhysicalMaterial({
      color: FAB_CLEAR_WHITE_GLASS.side,
      metalness: 0.02,
      roughness: 0.1,
      transmission: 0.92,
      thickness: 0.12,
      ior: 1.48,
      transparent: true,
      opacity: Math.min(0.28, opacity * 0.32),
      envMapIntensity: 0.45,
      specularIntensity: 0.45,
      attenuationColor: new THREE.Color('#E2E8F0'),
      attenuationDistance: 4.5,
    });
    applyGlassRenderHints(mat);
    physicalCache.set(key, mat);
  }
  return mat;
}

/** Instanced / distant white glass — no transmission shader cost. */
export function createFabClearWhiteDistantGlass(opacity = 1): THREE.MeshStandardMaterial {
  return getSharedStandardMaterial({
    color: FAB_CLEAR_WHITE_GLASS_DISTANT.color,
    metalness: FAB_CLEAR_WHITE_GLASS_DISTANT.metalness,
    roughness: FAB_CLEAR_WHITE_GLASS_DISTANT.roughness,
    transparent: true,
    opacity: Math.min(FAB_CLEAR_WHITE_GLASS_DISTANT.opacity, opacity * 0.36),
    envMapIntensity: FAB_CLEAR_WHITE_GLASS_DISTANT.envMapIntensity,
    depthWrite: false,
    side: THREE.DoubleSide,
  });
}

/** Close-range navy blue stocker enclosure glass. */
export function createFabNavyPhysicalGlass(
  opacity = 1,
  cacheKey: string
): THREE.MeshPhysicalMaterial {
  const key = `${cacheKey}-navy:v1:${opacity.toFixed(2)}`;
  let mat = physicalCache.get(key);
  if (!mat) {
    mat = new THREE.MeshPhysicalMaterial({
      color: FAB_NAVY_GLASS.primary,
      metalness: FAB_NAVY_GLASS.metalness,
      roughness: FAB_NAVY_GLASS.roughness,
      transmission: FAB_NAVY_GLASS.transmission,
      thickness: FAB_NAVY_GLASS.thickness,
      ior: FAB_NAVY_GLASS.ior,
      transparent: true,
      opacity: Math.min(FAB_NAVY_GLASS.opacity, opacity * 0.76),
      envMapIntensity: FAB_NAVY_GLASS.envMapIntensity,
      specularIntensity: 0.76,
      attenuationColor: new THREE.Color(FAB_NAVY_GLASS.attenuation),
      attenuationDistance: FAB_NAVY_GLASS.attenuationDistance,
    });
    applyGlassRenderHints(mat);
    physicalCache.set(key, mat);
  }
  return mat;
}

/** Side/back navy panels — slightly brighter blue tint. */
export function createFabNavySidePhysicalGlass(
  opacity = 1,
  cacheKey: string
): THREE.MeshPhysicalMaterial {
  const key = `${cacheKey}-navy-side:v1:${opacity.toFixed(2)}`;
  let mat = physicalCache.get(key);
  if (!mat) {
    mat = new THREE.MeshPhysicalMaterial({
      color: FAB_NAVY_GLASS.side,
      metalness: 0.09,
      roughness: 0.12,
      transmission: 0.44,
      thickness: 0.3,
      ior: 1.48,
      transparent: true,
      opacity: Math.min(0.7, opacity * 0.72),
      envMapIntensity: 0.8,
      specularIntensity: 0.72,
      attenuationColor: new THREE.Color('#122840'),
      attenuationDistance: 1.7,
    });
    applyGlassRenderHints(mat);
    physicalCache.set(key, mat);
  }
  return mat;
}

/** Instanced / distant navy glass — fleet LOD shell batches. */
export function createFabNavyDistantGlass(opacity = 1): THREE.MeshStandardMaterial {
  return getSharedStandardMaterial({
    color: FAB_NAVY_GLASS_DISTANT.color,
    metalness: FAB_NAVY_GLASS_DISTANT.metalness,
    roughness: FAB_NAVY_GLASS_DISTANT.roughness,
    transparent: true,
    opacity: Math.min(FAB_NAVY_GLASS_DISTANT.opacity, opacity * 0.66),
    envMapIntensity: FAB_NAVY_GLASS_DISTANT.envMapIntensity,
    depthWrite: false,
    side: THREE.DoubleSide,
  });
}

/** Side enclosure — cyan-blue glass sheets (reference stocker left/right faces). */
export function createFabLightBlueSidePhysicalGlass(
  opacity = 1,
  cacheKey: string
): THREE.MeshPhysicalMaterial {
  const key = `${cacheKey}-light-blue-side:v1:${opacity.toFixed(2)}`;
  let mat = physicalCache.get(key);
  if (!mat) {
    mat = new THREE.MeshPhysicalMaterial({
      color: FAB_LIGHT_BLUE_GLASS.side,
      metalness: 0.04,
      roughness: 0.05,
      transmission: 0.9,
      thickness: 0.14,
      ior: FAB_LIGHT_BLUE_GLASS.ior,
      transparent: true,
      opacity: Math.min(0.36, opacity * 0.38),
      envMapIntensity: 1.08,
      specularIntensity: 0.95,
      clearcoat: 0.1,
      clearcoatRoughness: 0.16,
      attenuationColor: new THREE.Color(FAB_LIGHT_BLUE_GLASS.attenuation),
      attenuationDistance: 4.2,
    });
    applyGlassRenderHints(mat);
    physicalCache.set(key, mat);
  }
  return mat;
}

/** Close-range light blue shelf / interior glass. */
export function createFabLightBluePhysicalGlass(
  opacity = 1,
  cacheKey: string
): THREE.MeshPhysicalMaterial {
  const key = `${cacheKey}-light-blue:v2:${opacity.toFixed(2)}`;
  let mat = physicalCache.get(key);
  if (!mat) {
    mat = new THREE.MeshPhysicalMaterial({
      color: FAB_LIGHT_BLUE_GLASS.primary,
      metalness: FAB_LIGHT_BLUE_GLASS.metalness,
      roughness: FAB_LIGHT_BLUE_GLASS.roughness,
      transmission: FAB_LIGHT_BLUE_GLASS.transmission,
      thickness: FAB_LIGHT_BLUE_GLASS.thickness,
      ior: FAB_LIGHT_BLUE_GLASS.ior,
      transparent: true,
      opacity: Math.min(FAB_LIGHT_BLUE_GLASS.opacity, opacity * 0.54),
      envMapIntensity: FAB_LIGHT_BLUE_GLASS.envMapIntensity,
      specularIntensity: 0.88,
      clearcoat: 0.12,
      clearcoatRoughness: 0.18,
      attenuationColor: new THREE.Color(FAB_LIGHT_BLUE_GLASS.attenuation),
      attenuationDistance: FAB_LIGHT_BLUE_GLASS.attenuationDistance,
    });
    applyGlassRenderHints(mat);
    physicalCache.set(key, mat);
  }
  return mat;
}

/** Instanced / distant light blue glass — shelf hints at low LOD. */
export function createFabLightBlueDistantGlass(opacity = 1): THREE.MeshStandardMaterial {
  return getSharedStandardMaterial({
    color: FAB_LIGHT_BLUE_GLASS_DISTANT.color,
    metalness: FAB_LIGHT_BLUE_GLASS_DISTANT.metalness,
    roughness: FAB_LIGHT_BLUE_GLASS_DISTANT.roughness,
    transparent: true,
    opacity: Math.min(FAB_LIGHT_BLUE_GLASS_DISTANT.opacity, opacity * 0.48),
    envMapIntensity: FAB_LIGHT_BLUE_GLASS_DISTANT.envMapIntensity,
    depthWrite: false,
    side: THREE.DoubleSide,
  });
}

/** Distant side enclosure — lighter cyan tint, more see-through at fleet LOD. */
export function createFabLightBlueSideDistantGlass(opacity = 1): THREE.MeshStandardMaterial {
  return getSharedStandardMaterial({
    color: FAB_LIGHT_BLUE_GLASS.side,
    metalness: 0.05,
    roughness: 0.1,
    transparent: true,
    opacity: Math.min(0.34, opacity * 0.36),
    envMapIntensity: 0.92,
    depthWrite: false,
    side: THREE.DoubleSide,
  });
}

/** Close-range pista green shelf glass inside stockers. */
export function createFabPistaGreenPhysicalGlass(
  opacity = 1,
  cacheKey: string
): THREE.MeshPhysicalMaterial {
  const key = `${cacheKey}-pista-green:v1:${opacity.toFixed(2)}`;
  let mat = physicalCache.get(key);
  if (!mat) {
    mat = new THREE.MeshPhysicalMaterial({
      color: FAB_PISTA_GREEN_GLASS.primary,
      metalness: FAB_PISTA_GREEN_GLASS.metalness,
      roughness: FAB_PISTA_GREEN_GLASS.roughness,
      transmission: FAB_PISTA_GREEN_GLASS.transmission,
      thickness: FAB_PISTA_GREEN_GLASS.thickness,
      ior: FAB_PISTA_GREEN_GLASS.ior,
      transparent: true,
      opacity: Math.min(FAB_PISTA_GREEN_GLASS.opacity, opacity * 0.54),
      envMapIntensity: FAB_PISTA_GREEN_GLASS.envMapIntensity,
      specularIntensity: 0.76,
      attenuationColor: new THREE.Color(FAB_PISTA_GREEN_GLASS.attenuation),
      attenuationDistance: FAB_PISTA_GREEN_GLASS.attenuationDistance,
    });
    applyGlassRenderHints(mat);
    physicalCache.set(key, mat);
  }
  return mat;
}

/** Pista-green rack shelf — legacy; prefer sky-blue shelf glass. */
export function createFabShelfSkyBluePhysicalGlass(
  opacity = 1,
  cacheKey: string
): THREE.MeshPhysicalMaterial {
  const key = `${cacheKey}-shelf-sky:v2:${opacity.toFixed(2)}`;
  let mat = physicalCache.get(key);
  if (!mat) {
    mat = new THREE.MeshPhysicalMaterial({
      color: FAB_SHELF_SKY_BLUE_GLASS.primary,
      metalness: FAB_SHELF_SKY_BLUE_GLASS.metalness,
      roughness: FAB_SHELF_SKY_BLUE_GLASS.roughness,
      transmission: FAB_SHELF_SKY_BLUE_GLASS.transmission,
      thickness: FAB_SHELF_SKY_BLUE_GLASS.thickness,
      ior: FAB_SHELF_SKY_BLUE_GLASS.ior,
      transparent: true,
      opacity: Math.min(FAB_SHELF_SKY_BLUE_GLASS.opacity, opacity * 0.78),
      envMapIntensity: FAB_SHELF_SKY_BLUE_GLASS.envMapIntensity,
      specularIntensity: 0.84,
      attenuationColor: new THREE.Color(FAB_SHELF_SKY_BLUE_GLASS.attenuation),
      attenuationDistance: FAB_SHELF_SKY_BLUE_GLASS.attenuationDistance,
    });
    applyGlassRenderHints(mat);
    physicalCache.set(key, mat);
  }
  return mat;
}

/** @deprecated Use createFabShelfSkyBluePhysicalGlass */
export function createFabShelfPistaPhysicalGlass(
  opacity = 1,
  cacheKey: string
): THREE.MeshPhysicalMaterial {
  return createFabShelfSkyBluePhysicalGlass(opacity, cacheKey);
}

/** Instanced / distant pista green shelf glass. */
export function createFabPistaGreenDistantGlass(opacity = 1): THREE.MeshStandardMaterial {
  return getSharedStandardMaterial({
    color: FAB_PISTA_GREEN_GLASS_DISTANT.color,
    metalness: FAB_PISTA_GREEN_GLASS_DISTANT.metalness,
    roughness: FAB_PISTA_GREEN_GLASS_DISTANT.roughness,
    transparent: true,
    opacity: Math.min(FAB_PISTA_GREEN_GLASS_DISTANT.opacity, opacity * 0.48),
    envMapIntensity: FAB_PISTA_GREEN_GLASS_DISTANT.envMapIntensity,
    depthWrite: false,
    side: THREE.DoubleSide,
  });
}

/** Process tool chamber — dark baby pink tinted glass (reference). */
export const FAB_BABY_PINK_GLASS = {
  primary: FAB_COLORS.babyPinkGlass,
  side: FAB_COLORS.babyPinkGlassBright,
  attenuation: FAB_COLORS.babyPinkGlassDeep,
  metalness: 0.04,
  roughness: 0.08,
  transmission: 0.58,
  thickness: 0.34,
  ior: 1.49,
  opacity: 0.82,
  envMapIntensity: 0.92,
  attenuationDistance: 1.8,
} as const;

export const FAB_BABY_PINK_GLASS_DISTANT = {
  color: FAB_COLORS.babyPinkGlass,
  metalness: 0.06,
  roughness: 0.14,
  opacity: 0.62,
  envMapIntensity: 0.82,
} as const;

/** Close-range baby pink chamber glass — process equipment front window. */
export function createFabBabyPinkPhysicalGlass(
  opacity = 1,
  cacheKey: string
): THREE.MeshPhysicalMaterial {
  const key = `${cacheKey}-baby-pink:v1:${opacity.toFixed(2)}`;
  let mat = physicalCache.get(key);
  if (!mat) {
    mat = new THREE.MeshPhysicalMaterial({
      color: FAB_BABY_PINK_GLASS.primary,
      metalness: FAB_BABY_PINK_GLASS.metalness,
      roughness: FAB_BABY_PINK_GLASS.roughness,
      transmission: FAB_BABY_PINK_GLASS.transmission,
      thickness: FAB_BABY_PINK_GLASS.thickness,
      ior: FAB_BABY_PINK_GLASS.ior,
      transparent: true,
      opacity: Math.min(FAB_BABY_PINK_GLASS.opacity, opacity * 0.86),
      envMapIntensity: FAB_BABY_PINK_GLASS.envMapIntensity,
      specularIntensity: 0.74,
      attenuationColor: new THREE.Color(FAB_BABY_PINK_GLASS.attenuation),
      attenuationDistance: FAB_BABY_PINK_GLASS.attenuationDistance,
    });
    applyGlassRenderHints(mat);
    physicalCache.set(key, mat);
  }
  return mat;
}

/** Side accent panels — lighter baby pink tint. */
export function createFabBabyPinkSidePhysicalGlass(
  opacity = 1,
  cacheKey: string
): THREE.MeshPhysicalMaterial {
  const key = `${cacheKey}-baby-pink-side:v1:${opacity.toFixed(2)}`;
  let mat = physicalCache.get(key);
  if (!mat) {
    mat = new THREE.MeshPhysicalMaterial({
      color: FAB_BABY_PINK_GLASS.side,
      metalness: 0.05,
      roughness: 0.12,
      transmission: 0.46,
      thickness: 0.3,
      ior: 1.47,
      transparent: true,
      opacity: Math.min(0.66, opacity * 0.68),
      envMapIntensity: 0.84,
      specularIntensity: 0.7,
      attenuationColor: new THREE.Color(FAB_BABY_PINK_GLASS.attenuation),
      attenuationDistance: 2.0,
    });
    applyGlassRenderHints(mat);
    physicalCache.set(key, mat);
  }
  return mat;
}

/** Instanced / distant baby pink glass — fleet LOD shell batches. */
export function createFabBabyPinkDistantGlass(opacity = 1): THREE.MeshStandardMaterial {
  return getSharedStandardMaterial({
    color: FAB_BABY_PINK_GLASS_DISTANT.color,
    metalness: FAB_BABY_PINK_GLASS_DISTANT.metalness,
    roughness: FAB_BABY_PINK_GLASS_DISTANT.roughness,
    transparent: true,
    opacity: Math.min(FAB_BABY_PINK_GLASS_DISTANT.opacity, opacity * 0.64),
    envMapIntensity: FAB_BABY_PINK_GLASS_DISTANT.envMapIntensity,
    depthWrite: false,
    side: THREE.DoubleSide,
  });
}

/** Instanced / distant smoked glass — no transmission shader cost. */
export function createFabSmokedDistantGlass(opacity = 1): THREE.MeshStandardMaterial {
  return getSharedStandardMaterial({
    color: FAB_SMOKED_GLASS_DISTANT.color,
    metalness: FAB_SMOKED_GLASS_DISTANT.metalness,
    roughness: FAB_SMOKED_GLASS_DISTANT.roughness,
    transparent: true,
    opacity: Math.min(FAB_SMOKED_GLASS_DISTANT.opacity, opacity * 0.6),
    envMapIntensity: FAB_SMOKED_GLASS_DISTANT.envMapIntensity,
    depthWrite: false,
    side: THREE.DoubleSide,
  });
}

/** Close-range neutral gray physical glass — stocker top glass / roof. */
export function createFabGrayPhysicalGlass(
  opacity = 1,
  cacheKey = 'fab-gray-glass'
): THREE.MeshPhysicalMaterial {
  const key = `${cacheKey}:v2:${opacity.toFixed(2)}`;
  let mat = physicalCache.get(key);
  if (!mat) {
    mat = new THREE.MeshPhysicalMaterial({
      color: FAB_GRAY_GLASS.primary,
      metalness: FAB_GRAY_GLASS.metalness,
      roughness: FAB_GRAY_GLASS.roughness,
      transmission: FAB_GRAY_GLASS.transmission,
      thickness: FAB_GRAY_GLASS.thickness,
      ior: FAB_GRAY_GLASS.ior,
      transparent: true,
      opacity: Math.min(FAB_GRAY_GLASS.opacity, opacity * 0.6),
      envMapIntensity: FAB_GRAY_GLASS.envMapIntensity,
      specularIntensity: 0.85,
      clearcoat: 0.15,
      clearcoatRoughness: 0.12,
      attenuationColor: new THREE.Color(FAB_GRAY_GLASS.attenuation),
      attenuationDistance: FAB_GRAY_GLASS.attenuationDistance,
    });
    applyGlassRenderHints(mat);
    physicalCache.set(key, mat);
  }
  return mat;
}

/** Instanced / distant neutral gray glass. */
export function createFabGrayDistantGlass(opacity = 1): THREE.MeshStandardMaterial {
  return getSharedStandardMaterial({
    color: FAB_GRAY_GLASS_DISTANT.color,
    metalness: FAB_GRAY_GLASS_DISTANT.metalness,
    roughness: FAB_GRAY_GLASS_DISTANT.roughness,
    transparent: true,
    opacity: Math.min(FAB_GRAY_GLASS_DISTANT.opacity, opacity * 0.55),
    envMapIntensity: FAB_GRAY_GLASS_DISTANT.envMapIntensity,
    depthWrite: false,
    side: THREE.DoubleSide,
  });
}

